import React, { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const SettingsCtx = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [pageSeoMap, setPageSeoMap] = useState({});

  const refresh = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/settings"),
        api.get("/page-seo"),
      ]);
      setSettings(s.data);
      const map = {};
      (p.data || []).forEach((it) => { map[it.slug] = it; });
      setPageSeoMap(map);
    } catch (e) {
      // fail silently — site still renders
    }
  };

  useEffect(() => { refresh(); }, []);

  // Inject Google Analytics
  useEffect(() => {
    if (!settings?.google_analytics_id) return;
    const id = settings.google_analytics_id;
    if (document.getElementById("ga4-script")) return;
    const s1 = document.createElement("script");
    s1.id = "ga4-script";
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.id = "ga4-init";
    s2.innerHTML = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${id}');`;
    document.head.appendChild(s2);
  }, [settings?.google_analytics_id]);

  // Inject GTM
  useEffect(() => {
    if (!settings?.google_tag_manager_id) return;
    const id = settings.google_tag_manager_id;
    if (document.getElementById("gtm-script")) return;
    const s = document.createElement("script");
    s.id = "gtm-script";
    s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`;
    document.head.appendChild(s);
  }, [settings?.google_tag_manager_id]);

  // Inject Facebook pixel
  useEffect(() => {
    if (!settings?.facebook_pixel_id) return;
    const id = settings.facebook_pixel_id;
    if (document.getElementById("fb-pixel")) return;
    const s = document.createElement("script");
    s.id = "fb-pixel";
    s.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${id}');fbq('track','PageView');`;
    document.head.appendChild(s);
  }, [settings?.facebook_pixel_id]);

  // Custom head HTML
  useEffect(() => {
    if (!settings?.custom_head_html) return;
    const existing = document.getElementById("custom-head-html");
    if (existing) existing.remove();
    const wrap = document.createElement("div");
    wrap.id = "custom-head-html";
    wrap.innerHTML = settings.custom_head_html;
    document.head.appendChild(wrap);
  }, [settings?.custom_head_html]);

  return (
    <SettingsCtx.Provider value={{ settings, pageSeoMap, refresh }}>
      {children}
    </SettingsCtx.Provider>
  );
}

export const useSettings = () => useContext(SettingsCtx) || { settings: null, pageSeoMap: {} };
